import Cookies from 'js-cookie';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import FilterChips from 'src/components/filter-chips/filter-chips';
import Cards from 'src/components/form/search/cards/cards';
import PageHeader from 'src/components/page-header/page-header';
import PaginatedFooter from 'src/components/paginated-footer/paginated-footer';
import { WORK_EMPLOYMENT_TYPE } from 'src/constants/filters';
import { useResize } from 'src/hooks/shared-hooks';
import { Branch, ChipsType, Tag, VacancyModel, WorkChipsFilter, WorkFilters } from 'src/types';
import { fireEvent } from 'src/utils/analytics/google';
import { useGetBranches, useGetTags, useGetVacancies } from './api/queries';
import EmptyPlaceholder from './empty-placeholder';
import WorkFilter from './form/filters';
import SearchForm from './form/form';
import css from './job-search.module.scss';
import Vacancy from './vacancy';

export default function JobSearch() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchValue = searchParams?.get('search');
  const searchBranch = searchParams?.get('branch')?.split('&');
  const searchTag = searchParams?.get('tag')?.split('&');
  const searchRank = searchParams?.get('rank')?.split('&');
  const selectedVacancy = document?.getElementById(`${Cookies?.get('card')}`) ?? null;

  const dimensions = useResize();
  const mobileMode = dimensions.width < 500;

  const [showFilter, setShowFilter] = useState(!mobileMode);

  const skeletons = [...Array(6).keys()].map((i) => <span key={i} className={css.skeleton} />);
  const { data: tags = [] } = useGetTags();
  const { data: branches = [] } = useGetBranches();

  const [filters, setFilters] = useState<WorkFilters>({
    search: searchValue ?? '',
    rank: searchRank
      ? WORK_EMPLOYMENT_TYPE.filter((r) => searchRank.includes(r.name))?.map((r) => r.id.toString())
      : [],
    branch: searchBranch ? branches.filter((b: Branch) => searchBranch.includes(b.name))?.map((b: Branch) => b.id) : [],
    tags: searchTag ? tags.filter((t: Tag) => searchTag.includes(t.name))?.map((t: Tag) => t.id) : [],
    page: 1,
    limit: 15
  });
  const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage } = useGetVacancies(filters);

  const vacancies = data?.pages.flatMap((page) => page?.items) || [];
  const totalCount = data?.pages[0]?.totalCount ?? 0;

  const selectedFilters = branches
    ?.filter((b: Branch) => filters.branch.includes(b.id))
    .map((b: Branch) => {
      return { id: b.id, name: b.name, type: ChipsType.branch };
    })
    .concat(
      tags
        ?.filter((t: Tag) => t.id && filters.tags.includes(t.id))
        .map((b: Tag) => {
          return { id: b.id, name: b.name, type: ChipsType.tag };
        })
    )
    .concat(
      filters.rank?.map((r) => {
        return {
          id: r,
          name: `${WORK_EMPLOYMENT_TYPE.find((w) => w.id.toString() === r)?.name}`,
          type: ChipsType.rank
        };
      })
    );

  const title = selectedFilters?.length > 0 ? `Знайдено ${totalCount} результатів:` : 'Усі вакансії:';

  const updateFilterValues = (data: Partial<WorkFilters>) =>
    setFilters({ ...filters, search: data.search ? data.search?.trim() : filters.search, ...data, page: 1 });

  const hidePaginatedFooter = isFetching || vacancies.length === 0 || !hasNextPage;

  function handleVacancyClick(vacancy: VacancyModel) {
    Cookies.set('card', vacancy.slug);
    fireEvent('view_vacancy', {
      title: vacancy.name,
      url: vacancy.slug
    });
    navigate(`/work/work-search/vacancy/${vacancy.slug}`);
  }

  const setSearchUrl = useCallback(
    (items: { name: string; value: string | null | undefined }[]) => {
      const nextSearchParams = new URLSearchParams(searchParams);
      for (const item of items) {
        item.value && item.value.length > 0
          ? nextSearchParams.set(item.name, item.value)
          : nextSearchParams.delete(item.name);
      }
      if (nextSearchParams.toString() !== searchParams.toString()) setSearchParams(nextSearchParams);
    },
    [searchParams, setSearchParams]
  );

  const clearFilters = () => setFilters({ ...filters, tags: [], branch: [], rank: [] });

  function deselectFilter(filter: WorkChipsFilter) {
    switch (filter.type) {
      case ChipsType.branch:
        setFilters({ ...filters, branch: filters.branch.filter((b: string) => b !== filter.id) });
        break;
      case ChipsType.tag:
        setFilters({ ...filters, tags: filters.tags.filter((t: string) => t !== filter.id) });
        break;
      case ChipsType.rank:
        setFilters({ ...filters, rank: filters.rank.filter((r: string) => r !== filter.id) });
        break;
      default:
        break;
    }
  }

  const changePopupState = useCallback(() => setShowFilter(!showFilter), [showFilter]);

  useEffect(() => {
    setShowFilter(!mobileMode);
  }, [mobileMode]);

  useEffect(() => {
    const branch: string = branches
      ?.filter((b: Branch) => filters.branch.includes(b.id))
      ?.map((b: Branch) => b.name)
      ?.join('&');
    const tag: string = tags
      ?.filter((t: Tag) => t.id && filters.tags.includes(t.id))
      ?.map((t: Tag) => t.name)
      ?.join('&');
    const rank: string = filters.rank
      ?.map((r) => `${WORK_EMPLOYMENT_TYPE.find((w) => w.id.toString() === r)?.name}`)
      ?.join('&');
    const valuesForParams: { [key: string]: string | null } = { search: filters.search, tag, branch, rank };
    const search = Object.keys(valuesForParams)?.map((key: string) => {
      return { name: key, value: valuesForParams[key]?.toString() };
    });
    setSearchUrl(search);
  }, [branches, filters, setSearchUrl, tags]);

  useEffect(() => {
    Cookies.remove('card');
    if (selectedVacancy) {
      selectedVacancy?.scrollIntoView({ behavior: 'auto', block: 'center' });
    } else {
      return;
    }
  }, [searchParams, searchValue, selectedVacancy, setSearchUrl]);

  return (
    <main className={css.jobSearch}>
      <PageHeader
        title='Пошук вакансій'
        description='Знайдіть роботу, яка відповідає Вашим потребам, навичкам та потенціалу'
        filter={
          <div className={css.filters}>
            <SearchForm
              initialValues={{ search: filters.search }}
              onSubmit={updateFilterValues}
              changePopupState={changePopupState}
            />
          </div>
        }
      />
      <div className={css.body}>
        <div className={css.vacanciesContainer}>
          <div className={css.filters}>
            {selectedFilters?.length > 0 && (
              <FilterChips selectedFilters={selectedFilters} onClick={deselectFilter} onClear={clearFilters} />
            )}
            {showFilter && (
              <WorkFilter
                initialValues={filters}
                clear={mobileMode ? clearFilters : undefined}
                close={changePopupState}
                tags={tags}
                branches={branches}
                onSubmit={updateFilterValues}
              />
            )}
          </div>
          <div className={css.cards}>
            {totalCount && <h3 className={css.title}>{title}</h3>}

            <Cards className={css.vacancies} loading={isFetching}>
              {!isFetching && vacancies?.length === 0 && <EmptyPlaceholder />}
              {vacancies?.map(
                (vacancy: VacancyModel) =>
                  vacancy && <Vacancy key={vacancy?.id} vacancy={vacancy} onClick={() => handleVacancyClick(vacancy)} />
              )}
              {isFetching && skeletons}
              {!hidePaginatedFooter && (
                <div className={css.pagerRow}>
                  <PaginatedFooter caption='Більше вакансій' disabled={isFetchingNextPage} onClick={fetchNextPage} />
                </div>
              )}
            </Cards>
          </div>
        </div>
      </div>
    </main>
  );
}
